import { computed, onUnmounted, ref, type Ref } from 'vue';
import { useI18n } from 'vue-i18n';

const SECOND = 1000;
const MINUTE = 60 * SECOND;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/**
 * Returns a localised relative time string and the absolute ISO for `title` attribute.
 * Refreshes every 60s to stay accurate.
 */
export const useRelativeTime = (timestamp: Ref<string | Date | null | undefined>) => {
  const { t } = useI18n();
  const now = ref(Date.now());

  const intervalId = setInterval(() => {
    now.value = Date.now();
  }, 60 * SECOND);

  onUnmounted(() => clearInterval(intervalId));

  const absolute = computed(() => {
    const v = timestamp.value;
    if (!v) return '';
    return v instanceof Date ? v.toISOString() : new Date(v).toISOString();
  });

  const relative = computed(() => {
    const v = timestamp.value;
    if (!v) return '';
    const ms = now.value - new Date(v).getTime();
    if (ms < MINUTE) return t('time.now');
    if (ms < HOUR) return t('time.minutes', { n: Math.floor(ms / MINUTE) });
    if (ms < DAY) return t('time.hours', { n: Math.floor(ms / HOUR) });
    if (ms < 2 * DAY) return t('time.yesterday');
    return t('time.days', { n: Math.floor(ms / DAY) });
  });

  return { relative, absolute };
};
